import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import './Clients.css';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const navigate = useNavigate();

  const showModal = (type, message, onConfirm = null) => {
    setModalType(type);
    setModalMessage(message);
    setConfirmAction(() => onConfirm);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setConfirmAction(null);
  };

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
    closeModal();
  };

  const checkTokenExpiration = () => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        handleLogout();
      }
    } else {
      handleLogout();
    }
  };

  const handleLogout = useCallback(() => {
    showModal('confirmation', 'Are you sure you want to log out?', () => {
      localStorage.removeItem('token');
      navigate('/login');
    });
  }, [navigate]);

  useEffect(() => {
    checkTokenExpiration();

    const intervalId = setInterval(() => {
      checkTokenExpiration();
    }, 60000);

    const fetchClients = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const sortedClients = response.data.sort((a, b) =>
          a.firstName.localeCompare(b.firstName)
        );
        setClients(sortedClients);
        setFilteredClients(sortedClients);
      } catch (error) {
        setErrorMessage('Failed to load clients. Please try again.');
        showModal('error', 'Failed to load clients. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchClients();

    return () => clearInterval(intervalId);
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = clients.filter(
      (client) =>
        client.firstName.toLowerCase().includes(value) ||
        client.lastName.toLowerCase().includes(value)
    );
    setFilteredClients(filtered);
  };

  const handleClientClick = (clientId) => {
    navigate(`/clientdashboard/${clientId}`);
  };

  const handleDeleteClient = (clientId) => {
    showModal('confirmation', 'Are you sure you want to remove this client from your practice?', async () => {
      const token = localStorage.getItem('token');
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const updatedClients = clients.filter((client) => client._id !== clientId);
        setClients(updatedClients);
        setFilteredClients(updatedClients);
        showModal('success', 'Client removed successfully from your practice.');
      } catch (error) {
        setErrorMessage('Failed to remove client. Please try again.');
        showModal('error', 'Failed to remove client. Please try again.');
      }
    });
  };

  const getClientInitials = (firstName, lastName) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatPhone = (phone) => {
    if (!phone) return 'No phone provided';
    // Simple phone formatting - you can enhance this based on your needs
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className="cl-page">

      {/* ── Nav ── */}
      <div className="cl-nav">
        <button className="cl-back-btn" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
      </div>

      {/* ── Header ── */}
      <div className="cl-header">
        <div>
          <h1 className="cl-title">Clients</h1>
          <p className="cl-subtitle">
            {clients.length} client{clients.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <button className="cl-add-btn" onClick={() => navigate('/createclient')}>
          + Add client
        </button>
      </div>

      {/* ── Search ── */}
      <div className="cl-search-wrap">
        <input
          type="text"
          className="cl-search"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={handleSearch}
        />
        {searchTerm && (
          <button className="cl-search-clear" onClick={() => { setSearchTerm(''); setFilteredClients(clients); }}>×</button>
        )}
      </div>
      {searchTerm && (
        <p className="cl-search-count">{filteredClients.length} result{filteredClients.length !== 1 ? 's' : ''}</p>
      )}

      {/* ── Error ── */}
      {errorMessage && <div className="cl-error">{errorMessage}</div>}

      {/* ── Client cards ── */}
      {loading ? (
        <div className="cl-loading">
          <div className="cl-spinner" />
          <p>Loading clients...</p>
        </div>
      ) : filteredClients.length > 0 ? (
        <ul className="cl-list">
          {filteredClients.map((client) => (
            <li key={client._id} className="cl-card" onClick={() => handleClientClick(client._id)}>
              <div className="cl-avatar">
                {getClientInitials(client.firstName, client.lastName)}
              </div>
              <div className="cl-info">
                <p className="cl-name">{client.firstName} {client.lastName}</p>
                <p className="cl-meta">{client.email}</p>
                {(client.company || client.title) && (
                  <p className="cl-meta">{[client.title, client.company].filter(Boolean).join(' · ')}</p>
                )}
                <p className="cl-meta">{formatPhone(client.phone)}</p>
              </div>
              <div className="cl-badges">
                <span className="cl-badge">Active</span>
                {client.circleId && <span className="cl-badge circle">✦ Shared</span>}
              </div>
              <button
                className="cl-remove-btn"
                onClick={(e) => { e.stopPropagation(); handleDeleteClient(client._id); }}
                title="Remove client"
              >
                🗑
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="cl-empty">
          <div className="cl-empty-icon">👥</div>
          <h3>No clients yet</h3>
          <p>
            {searchTerm
              ? `No clients found for "${searchTerm}"`
              : 'Add your first client to get started'}
          </p>
          {!searchTerm && (
            <button className="cl-add-btn" onClick={() => navigate('/createclient')}>
              + Add first client
            </button>
          )}
        </div>
      )}

      {/* ── Modal ── */}
      {modalVisible && (
        <div className="cl-modal-overlay" onClick={closeModal}>
          <div className="cl-modal" onClick={e => e.stopPropagation()}>
            <h2>
              {modalType === 'success' && '✅ Success'}
              {modalType === 'error' && '⚠️ Error'}
              {modalType === 'confirmation' && 'Are you sure?'}
            </h2>
            <p>{modalMessage}</p>
            <div className="cl-modal-actions">
              {modalType === 'confirmation' ? (
                <>
                  <button className="cl-modal-confirm" onClick={handleConfirm}>Yes, continue</button>
                  <button className="cl-modal-cancel" onClick={closeModal}>Cancel</button>
                </>
              ) : (
                <button className="cl-modal-cancel" onClick={closeModal}>Got it</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;